import { useState, useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'

import { LoginComponent } from './components'
import {
  SPARCK_REFRESH_USER_TOKEN,
  SPARCK_USER_TOKEN,
  SPARCK_USER_TOKEN_ID,
  SPARCK_USER_ID,
} from 'constants/app'
import { useUser } from 'contexts'
import { API_BASE_URL } from 'core/environments'
import { API } from 'core/requests'
import { ROUTES_PATHS } from 'core/routes'
import { t } from 'i18n'
import { LoginType } from 'types'
import { decodeJWT } from 'utils'
import { decodeQueryParams, urlParams } from 'utils/queryParams'
import { currentBuildVersion, LS_BUILD_KEY } from 'utils/requests'
import { saveUserCredentials, clearUserCredentials, updateLastLogin } from 'utils/storage'

import {
  saveBiometricToken,
  showBiometricPrompt,
  deleteBiometricToken,
  checkBiometricStatus,
  shouldUseBiometric,
  isMobileDevice
} from 'utils'

export default function Login() {
  const initialLoginState: LoginType = {
    email: '',
    password: '',
    token: null,
    remember_me: false,
  }

  const initialChangePasswordState: any = {
    current_password: null,
    new_password: '',
  }

  const { user, setUser } = useUser()!
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()

  const [loginState, setLoginState] = useState(initialLoginState)
  const [mfaDetails, setMfaDetails] = useState(null)
  const [changePasswordState, setChangePasswordState] = useState(initialChangePasswordState)

  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false)
  const [hasBiometricToken, setHasBiometricToken] = useState(false)
  const [biometricLoading, setBiometricLoading] = useState(false)

  const params: any = urlParams(history.location.search)
  const redirect = params.redirect && `/${params.redirect}`

  useEffect(() => {
    const checkBiometric = () => {
      if (shouldUseBiometric()) {
        checkBiometricStatus((status) => {
          setIsBiometricAvailable(status?.isSuccess || false)
          setHasBiometricToken(status?.hasSecret || false)
        })
      }
    }

    checkBiometric()
  }, [])

  const authenticateUser = (response: any, isBiometric = false) => {
    const decodedToken = decodeJWT(response.access_token)

    localStorage.setItem(SPARCK_USER_TOKEN, response.access_token)
    localStorage.setItem(SPARCK_USER_TOKEN_ID, response.id)
    localStorage.setItem(SPARCK_USER_ID, decodedToken.sub)

    if (isMobileDevice() || isBiometric || loginState.remember_me) {
      saveUserCredentials({
        email: loginState.email,
        password: loginState.password,
        rememberMe: true,
        lastLogin: Date.now()
      })
      updateLastLogin()

      if (shouldUseBiometric() && response.access_token && (isMobileDevice() || isBiometric)) {
        saveBiometricToken(response.access_token)
        setHasBiometricToken(true)
        enqueueSnackbar('Biometrics configured successfully!', {
          variant: 'success',
        })
      }
    } else {
      clearUserCredentials()
    }

    setUser({
      ...decodedToken,
      token: response.access_token,
    })

    if (isBiometric) {
      enqueueSnackbar('Biometric login successful!', {
        variant: 'success',
      })
    }
  }

  const handleBiometricLogin = () => {
    if (!shouldUseBiometric()) {
      enqueueSnackbar('Biometric login is not available on this device', { variant: 'warning' })
      return
    }

    setBiometricLoading(true)

    showBiometricPrompt((token) => {
      setBiometricLoading(false)

      if (token) {
        try {
          const decodedToken = decodeJWT(token)

          if (!decodedToken?.sub) {
            throw new Error('Invalid token')
          }

          authenticateUser({
            access_token: token,
            id: decodedToken.sub
          }, true)

        } catch (error) {
          console.error('Biometric login error:', error)
          localStorage.removeItem(SPARCK_USER_TOKEN)
          deleteBiometricToken()
          setHasBiometricToken(false)

          enqueueSnackbar('Invalid login. Please use your credentials', {
            variant: 'error',
          })
        }
      } else {
        enqueueSnackbar('Biometric authentication canceled!', {
          variant: 'info'
        })
      }
    })
  }

  const loginMutation = useMutation(
    (payload: any) => {
      // For mobile, always force remember_me = true
      const loginPayload = isMobileDevice()
        ? { ...payload, remember_me: true }
        : payload

      return API.create('login', loginPayload)
    },
    {
      onSuccess: (response: any) => {
        const responseData = response?.data

        if (responseData && responseData.access_token) {
          authenticateUser(responseData)
        }

        if (responseData && responseData.refresh_token) {
          localStorage.setItem(SPARCK_REFRESH_USER_TOKEN, responseData.refresh_token)
        }

        if (responseData && responseData.mfa_required) {
          setMfaDetails(responseData)
          setLoginState({
            ...loginState,
            factor_type: responseData.factor_type,
          })
        }
      },
      onError: (error: any) => {
        if (error?.message === 'token_expired') {
          if (hasBiometricToken) {
            deleteBiometricToken()
            setHasBiometricToken(false)
            enqueueSnackbar('Session expired. Please login again.', {
              variant: 'warning',
            })
          }

          history.push(ROUTES_PATHS.login)
          localStorage.removeItem(SPARCK_USER_TOKEN)
          localStorage.removeItem(SPARCK_USER_ID)
        }
      }
    }
  )

  const startMFAAuthenticationMutation = useMutation(
    (payload: any) => API.create('startMFAAuthentication', payload),
    {
      onSuccess: response => {
        setMfaDetails(response)
        setLoginState({
          ...loginState,
          factor_type: response.factor_type,
        })
      },
    },
  )

  const finishMFAAuthenticationMutation = useMutation(
    (payload: any) => API.create('finishMFAAuthentication', payload),
    {
      onSuccess: response => {
        if (response && response.access_token) {
          authenticateUser(response)
        }
      },
    },
  )

  const redirectIfNecessary = redirect
    ? `?redirect=${redirect.replace('/', '')}`
    : ''
  const loginRedirect = [ROUTES_PATHS.login, redirectIfNecessary].join('')

  const resendVerificationCodeMutation = useMutation((payload: any) =>
    API.create('createMFAFactor', payload),
  )

  const forgotPasswordMutation = useMutation(
    (payload: any) =>
      API.create('sendRecoveryInstructions', payload, {
        queryParams: { email: payload.email },
      }),
    {
      onSettled: (response: any) => {
        if (response && response.success) {
          history.push(loginRedirect)

          enqueueSnackbar(t('login.snackbar.success.checkEmail'), {
            variant: 'success',
          })
        }
      },
    },
  )

  const recoverPasswordMutation = useMutation(
    (payload: any) => API.create('passwordRecovery', payload),
    {
      onSettled: (response: any) => {
        if (response && response.id) {
          history.push(loginRedirect)

          enqueueSnackbar(t('login.snackbar.success.passwordUpdated'), {
            variant: 'success',
          })
        }
      },
    },
  )

  const changePasswordMutation = useMutation(
    (payload: any) =>
      API.update('changePassword', payload, {
        pathParams: { id: payload.user_id },
      }),
    {
      onSettled: (response: any) => {
        if (response && !response.errors) {
          history.push(loginRedirect)

          enqueueSnackbar(t('login.snackbar.success.passwordUpdated'), {
            variant: 'success',
          })
        }
      },
    },
  )

  const changePassword = () => {
    changePasswordMutation.mutate({
      user: changePasswordState,
      user_id: user.id,
    })
  }

  useEffect(() => {
    const queryParams = decodeQueryParams(history.location.search)

    if (queryParams && queryParams.token) {
      setLoginState({ ...loginState, token: queryParams.token })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history])

  useEffect(() => {
    fetch(API_BASE_URL.replace('v1/', ''), {
      headers: {
        'X-Front-End-Build-Version': currentBuildVersion(),
      } as any,
      encoding: null,
      mode: 'cors',
      method: 'GET',
    } as any)
      .then(response => response?.json())
      .then(response => {
        const reloadRequired = response?.reload

        if (reloadRequired) {
          localStorage.setItem(LS_BUILD_KEY, response.versions.current)
          window.location.reload()
        }
      })
  }, [])

  return (
    <LoginComponent
      loginState={loginState}
      setLoginState={setLoginState}
      mfaDetails={mfaDetails}
      loginMutation={loginMutation}
      resendVerificationCodeMutation={resendVerificationCodeMutation}
      forgotPasswordMutation={forgotPasswordMutation}
      recoverPasswordMutation={recoverPasswordMutation}
      startMFAAuthenticationMutation={startMFAAuthenticationMutation}
      finishMFAAuthenticationMutation={finishMFAAuthenticationMutation}
      changePasswordState={changePasswordState}
      setChangePasswordState={setChangePasswordState}
      changePassword={changePassword}
      changePasswordIsLoading={changePasswordMutation.isLoading}
      redirect={redirect}
      isBiometricAvailable={isBiometricAvailable}
      hasBiometricToken={hasBiometricToken}
      onBiometricLogin={handleBiometricLogin}
      biometricLoading={biometricLoading}
    />
  )
}
